import { useCallbackRef } from '@chakra-ui/react';
import { Conversation, Message } from '@twilio/conversations';
import { useState } from 'react';
import useSWR, { useSWRConfig } from 'swr';

import { fetchConversationMessages } from '@/fetchers/conversation';
import useSWRSubscription from '@/hooks/useSWRSubscription';
import { IConversationMessage, IMessageFetcherOptions } from '@/utils/conversation/types';

const defaultUseMessagesOptions: IMessageFetcherOptions = {
  pageSize: 10,
};

export const useMessages = (
  conversation: Conversation,
  options: IMessageFetcherOptions = defaultUseMessagesOptions
) => {
  const { onMessageAdded, onMessageRemoved } = options;
  const { cache } = useSWRConfig();

  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const swr = useSWR(conversation?.sid, () => fetchConversationMessages(conversation, options), {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  });

  const subscribe = (key, { next }) => {
    const addedHandler = (message: Message) => {
      const { messagePaginator, messages } = cache.get(key);

      onMessageAdded?.(message as IConversationMessage);
      messages.add(message);

      next(undefined, { messagePaginator, messages: messages });
    };

    const updateHandler = ({ message }) => {
      const { messagePaginator, messages } = cache.get(key);

      messages.update(message);

      next(undefined, { messagePaginator, messages: messages });
    };

    const removeHandler = (message) => {
      const { messagePaginator, messages } = cache.get(key);

      messages.remove(message);
      onMessageRemoved?.(message as IConversationMessage, messages.items);

      next(undefined, { messagePaginator, messages: messages });
    };

    conversation?.on('messageAdded', addedHandler);
    conversation?.on('messageUpdated', updateHandler);
    conversation?.on('messageRemoved', removeHandler);
    return () => {
      conversation?.off('messageAdded', addedHandler);
      conversation?.off('messageUpdated', updateHandler);
      conversation?.off('messageRemoved', removeHandler);
    };
  };

  useSWRSubscription(conversation?.sid, subscribe);

  const loadMore = useCallbackRef(async () => {
    if (!isLoadingMore && swr?.data?.messagePaginator?.hasPrevPage) {
      setIsLoadingMore(true);

      await swr.mutate(async (data) => {
        if (data.messagePaginator.hasPrevPage) {
          const prevPagePaginator = await data.messagePaginator.prevPage();

          data.messages.add(prevPagePaginator.items);
          return {
            messages: data.messages,
            messagePaginator: prevPagePaginator,
          };
        }

        return data;
      }, false);

      setIsLoadingMore(false);
    }
  }, [swr]);

  return {
    get messages() {
      return swr.data?.messages?.items;
    },
    get error() {
      return swr.error;
    },
    get isLoadingMore() {
      return isLoadingMore;
    },
    get isReachingEnd() {
      return !swr?.data?.messagePaginator?.hasPrevPage;
    },
    get isLoadingMessages() {
      return swr.isValidating && !swr.data;
    },
    loadMore,
  };
};
